import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { Search, Plus, Trash2, Power, Edit2, Info } from 'lucide-react';

function AdminFeatureFlags() {
    const [searchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page')) || 1;

    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFlag, setEditingFlag] = useState(null);
    const [formData, setFormData] = useState({
        key: '',
        description: '',
        isEnabled: false,
        workspaces: '',
        users: '',
        rolloutPercentage: ''
    });

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadFlags = useCallback(async () => {
        try {
            setLoading(true);
            const response = await adminManagementService.getFeatureFlags({
                search: debouncedSearch,
                page: currentPage
            });
            setFlags(response || []);
        } catch (error) {
            console.error('Failed to load feature flags', error);
            setFlags([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearch]);

    useEffect(() => {
        loadFlags();
    }, [loadFlags]);

    const handleOpenCreate = () => {
        setEditingFlag(null);
        setFormData({
            key: '',
            description: '',
            isEnabled: false,
            workspaces: '',
            users: '',
            rolloutPercentage: ''
        });
        setError(null);
        setSuccess(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (flag) => {
        setEditingFlag(flag);
        const rules = flag.rules || {};
        setFormData({
            key: flag.key,
            description: flag.description || '',
            isEnabled: flag.isEnabled,
            workspaces: Array.isArray(rules.workspaces) ? rules.workspaces.join(', ') : '',
            users: Array.isArray(rules.users) ? rules.users.join(', ') : '',
            rolloutPercentage: rules.rolloutPercentage !== undefined && rules.rolloutPercentage !== null ? String(rules.rolloutPercentage) : ''
        });
        setError(null);
        setSuccess(null);
        setIsModalOpen(true);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const rules = {};

            // Parse workspaces whitelist
            if (formData.workspaces.trim()) {
                rules.workspaces = formData.workspaces
                    .split(',')
                    .map(w => w.trim())
                    .filter(Boolean);
            }

            // Parse users whitelist
            if (formData.users.trim()) {
                rules.users = formData.users
                    .split(',')
                    .map(u => u.trim())
                    .filter(Boolean);
            }

            // Parse rollout percentage
            if (formData.rolloutPercentage.trim()) {
                const pct = parseInt(formData.rolloutPercentage, 10);
                if (isNaN(pct) || pct < 0 || pct > 100) {
                    throw new Error('Rollout percentage must be a number between 0 and 100');
                }
                rules.rolloutPercentage = pct;
            }

            const payload = {
                key: formData.key.trim(),
                description: formData.description.trim(),
                isEnabled: formData.isEnabled,
                rules
            };

            if (editingFlag) {
                await adminManagementService.updateFeatureFlag(editingFlag.id, payload);
                setSuccess('Feature flag updated successfully');
            } else {
                await adminManagementService.createFeatureFlag(payload);
                setSuccess('Feature flag created successfully');
            }

            await loadFlags();
            setTimeout(() => setIsModalOpen(false), 1200);
        } catch (err) {
            setError(err.message || err.response?.data?.error || 'Failed to save feature flag');
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleActive = async (flag) => {
        try {
            await adminManagementService.updateFeatureFlag(flag.id, {
                isEnabled: !flag.isEnabled
            });
            await loadFlags();
        } catch (error) {
            console.error('Failed to toggle feature flag status', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this feature flag?')) return;
        try {
            await adminManagementService.deleteFeatureFlag(id);
            await loadFlags();
        } catch (error) {
            console.error('Failed to delete feature flag', error);
        }
    };

    const formatRulesSummary = (rules) => {
        if (!rules) return 'None';
        const parts = [];
        if (rules.workspaces && rules.workspaces.length > 0) {
            parts.push(`${rules.workspaces.length} workspaces`);
        }
        if (rules.users && rules.users.length > 0) {
            parts.push(`${rules.users.length} users`);
        }
        if (rules.rolloutPercentage !== undefined && rules.rolloutPercentage !== null) {
            parts.push(`${rules.rolloutPercentage}% rollout`);
        }
        return parts.length > 0 ? parts.join(', ') : 'None';
    };

    if (loading && flags.length === 0) return <div className="admin-loading">Loading feature flags...</div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h2>Feature Flag Management ({flags.length})</h2>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="admin-search-bar">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search flags..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="admin-add-btn btn btn-primary" onClick={handleOpenCreate}>
                        <Plus size={18} />
                        Create Flag
                    </button>
                </div>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Flag Key</th>
                            <th>Description</th>
                            <th>Status</th>
                            <th>Target Rules</th>
                            <th>Last Updated</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {flags.map(flag => (
                            <tr key={flag.id} style={{ opacity: flag.isEnabled ? 1 : 0.65 }}>
                                <td>
                                    <strong style={{ fontFamily: 'monospace', color: '#38bdf8' }}>{flag.key}</strong>
                                </td>
                                <td>{flag.description || '-'}</td>
                                <td>
                                    <span className={`status-badge ${flag.isEnabled ? 'success' : 'warning'}`}>
                                        {flag.isEnabled ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                        {formatRulesSummary(flag.rules)}
                                    </span>
                                </td>
                                <td>
                                    {new Date(flag.updatedAt).toLocaleDateString()}
                                </td>
                                <td>
                                    <div className="admin-actions">
                                        <button className="icon-btn" onClick={() => handleOpenEdit(flag)} title="Edit">
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            className={`icon-btn ${flag.isEnabled ? 'warning-text' : 'success-text'}`}
                                            onClick={() => handleToggleActive(flag)}
                                            title={flag.isEnabled ? 'Deactivate' : 'Activate'}
                                        >
                                            <Power size={16} />
                                        </button>
                                        <button className="icon-btn danger-text" onClick={() => handleDelete(flag.id)} title="Delete">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {flags.length === 0 && (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                                    No feature flags found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <div className="admin-modal-header">
                            <h3>{editingFlag ? `Edit Flag: ${editingFlag.key}` : 'Create New Feature Flag'}</h3>
                            <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
                        </div>
                        <form onSubmit={handleSave} className="admin-modal-body">
                            {error && <div className="admin-form-error" style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</div>}
                            {success && <div className="admin-form-success" style={{ color: '#10b981', marginBottom: '1rem' }}>{success}</div>}

                            <div className="form-section" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>Flag Key</label>
                                    <input
                                        type="text"
                                        name="key"
                                        placeholder="e.g. enable-live-sessions"
                                        value={formData.key}
                                        onChange={handleFormChange}
                                        disabled={!!editingFlag}
                                        required
                                        style={{
                                            backgroundColor: '#0f172a',
                                            border: '1px solid #334155',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            color: '#f8fafc',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>Description</label>
                                    <textarea
                                        name="description"
                                        placeholder="What does this feature flag toggle?"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        rows={3}
                                        style={{
                                            backgroundColor: '#0f172a',
                                            border: '1px solid #334155',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            color: '#f8fafc',
                                            outline: 'none',
                                            resize: 'none'
                                        }}
                                    />
                                </div>
                                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                                    <input
                                        type="checkbox"
                                        id="isEnabled"
                                        name="isEnabled"
                                        checked={formData.isEnabled}
                                        onChange={handleFormChange}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <label htmlFor="isEnabled" style={{ color: '#cbd5e1', cursor: 'pointer', userSelect: 'none' }}>
                                        Globally Enable Flag
                                    </label>
                                </div>
                            </div>

                            <div className="form-section" style={{ borderTop: '1px solid #334155', marginTop: '1.5rem', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <h4 style={{ color: '#f8fafc', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Targeting Rules
                                    <span title="Optional whitelist rules that override or limit flag targets" style={{ display: 'inline-flex', cursor: 'help' }}>
                                        <Info size={14} color="#94a3b8" />
                                    </span>
                                </h4>
                                
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>Workspace Whitelist</label>
                                    <input
                                        type="text"
                                        name="workspaces"
                                        placeholder="Comma-separated Workspace IDs (UUIDs)"
                                        value={formData.workspaces}
                                        onChange={handleFormChange}
                                        style={{
                                            backgroundColor: '#0f172a',
                                            border: '1px solid #334155',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            color: '#f8fafc',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>User Whitelist</label>
                                    <input
                                        type="text"
                                        name="users"
                                        placeholder="Comma-separated User emails or IDs"
                                        value={formData.users}
                                        onChange={handleFormChange}
                                        style={{
                                            backgroundColor: '#0f172a',
                                            border: '1px solid #334155',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            color: '#f8fafc',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>Rollout Percentage (0 - 100)</label>
                                    <input
                                        type="number"
                                        name="rolloutPercentage"
                                        min="0"
                                        max="100"
                                        placeholder="e.g. 50 (leave empty for 100%)"
                                        value={formData.rolloutPercentage}
                                        onChange={handleFormChange}
                                        style={{
                                            backgroundColor: '#0f172a',
                                            border: '1px solid #334155',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            color: '#f8fafc',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="admin-modal-footer" style={{ marginTop: '2rem' }}>
                                <button type="button" className="admin-cancel-btn" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="admin-save-btn btn btn-primary" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : (editingFlag ? 'Save Changes' : 'Create Flag')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminFeatureFlags;
